import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, Alert } from '@mui/material';
import { XIcon } from 'lucide-react';
interface UserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
}
interface FormData {
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount: string;
  designation: string;
  country: string;
  passportId: string;
}
interface FormErrors {
  fullName?: string;
  username?: string;
  email?: string;
  mobile?: string;
  branchCode?: string;
  settlementAccount?: string;
  designation?: string;
  country?: string;
  passportId?: string;
}
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
const COUNTRIES = ['Ghana', 'Nigeria', 'Senegal', 'Uganda', 'Kenya', "Côte d'Ivoire", 'Togo', 'Cameroon', 'Benin'];
// Simulated OTP hashing function (in production, this would be done server-side)
const hashOTP = (otp: string): string => {
  // This is a placeholder - in production, use bcrypt or similar
  return `hashed_${otp}_${Date.now()}`;
};
const UserFormDrawer: React.FC<UserFormProps> = ({
  open,
  user,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: '',
    country: '',
    passportId: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showOTPInfo, setShowOTPInfo] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        username: user.username || '',
        email: user.email || '',
        mobile: user.mobile || '',
        branchCode: user.branchCode || '',
        settlementAccount: user.settlementAccount || '',
        designation: user.designation || '',
        country: user.country || '',
        passportId: user.passportId || ''
      });
      setShowOTPInfo(false);
    } else {
      setFormData({
        fullName: '',
        username: '',
        email: '',
        mobile: '',
        branchCode: '',
        settlementAccount: '',
        designation: '',
        country: '',
        passportId: ''
      });
      setShowOTPInfo(true);
    }
    setErrors({});
  }, [user, open]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\+\d{7,15}$/.test(formData.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = 'International format required, e.g. +233 20 123 4567';
    }
    // Optional fields - no validation required
    // branchCode, settlementAccount, and designation can be empty
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      const userData = {
        ...formData,
        id: user?.id
      };
      // If creating a new user, generate OTP
      if (!user) {
        const tempOTP = 'Test01';
        const hashedOTP = hashOTP(tempOTP);
        userData.hashedPassword = hashedOTP;
        userData.firstLoginRequired = true;
        userData.tempOTP = tempOTP; // For display purposes only
      }
      onSave(userData);
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-2554">
      <Box sx={{
      p: 3
    }} data-id="element-2555">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2556">
          <Typography variant="h6" data-id="element-2557">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-2558">
            <XIcon size={18} data-id="element-2559" />
          </IconButton>
        </Box>
        {showOTPInfo && <Alert severity="info" sx={{
        mb: 2
      }} data-id="element-2560">
            A temporary password (OTP: Test01) will be generated. User must
            reset password on first login.
          </Alert>}
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-2561">
          <TextField fullWidth margin="normal" label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} error={!!errors.fullName} helperText={errors.fullName} required data-id="element-2562" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-2563" />
          <TextField fullWidth margin="normal" label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-2564" />
          <TextField fullWidth margin="normal" label="Mobile Number" name="mobile" value={formData.mobile} onChange={handleChange} error={!!errors.mobile} helperText={errors.mobile || 'International format, e.g. +233 20 123 4567'} placeholder="+233 20 123 4567" required data-id="element-2565" />
          <TextField fullWidth margin="normal" label="Passport/ID (Optional)" name="passportId" value={formData.passportId} onChange={handleChange} error={!!errors.passportId} helperText={errors.passportId} placeholder="e.g. GH1234567" data-id="element-2566" />
          <FormControl fullWidth margin="normal" data-id="element-2567">
            <InputLabel id="country-label" data-id="element-2568">Country (Optional)</InputLabel>
            <Select labelId="country-label" name="country" value={formData.country} onChange={handleChange} label="Country (Optional)" data-id="element-2569">
              <MenuItem value="" data-id="element-2570">
                <em data-id="element-2571">None</em>
              </MenuItem>
              {COUNTRIES.map(country => <MenuItem key={country} value={country} data-id="element-2572">
                  {country}
                </MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" data-id="element-2573">
            <InputLabel id="branch-code-label" data-id="element-2574">
              Branch Code (Optional)
            </InputLabel>
            <Select labelId="branch-code-label" name="branchCode" value={formData.branchCode} onChange={handleChange} label="Branch Code (Optional)" data-id="element-2575">
              <MenuItem value="" data-id="element-2576">
                <em data-id="element-2577">None</em>
              </MenuItem>
              {BRANCH_CODES.map(code => <MenuItem key={code} value={code} data-id="element-2578">
                  {code}
                </MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Settlement Account (Optional)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} data-id="element-2579" />
          <TextField fullWidth margin="normal" label="Designation (Optional)" name="designation" value={formData.designation} onChange={handleChange} data-id="element-2580" />
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-2581">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2582">
          <Button variant="outlined" onClick={onClose} data-id="element-2583">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-2584">
            {user ? 'Save Changes' : 'Create User'}
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default UserFormDrawer;